import * as React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IRootState } from "../../../reducer/CombineReducer";
import { ErrorAlertSlice } from "../../ErrorAlert/Index";
import { ExtractModes, IExtract } from "../ExtractAdd/redux/Types";
import { FinancialSlice } from "../FinancialYearList";
import {
  getExtractListBasedOnSearchInput,
} from "../../Common/TreeList/Helper/Script";
import { ExtractAddSlice } from "../ExtractAdd";
import { ErrorAlertType, IErrorAlertObject } from "../../ErrorAlert/redux/Types";
import { onExtractCancelClick, validateCloseEditExtract } from "../../RightPanel/ExtractDetail/Helper";
import { IExtractList, IGetExtractDetails } from "./redux/Types";
import ExtractTreeListView from "./ExtractTreeList.view";
import { GetExtractList } from "./Service";
import { sortFunction } from "./Helper/helper";
import { ExtractListSlice } from "./index";

/**
 * Responsible for rendering the Extract List
 */
export const ExtractTreeList: React.FC = () => {

  const [
    extractAdd,
    yearsState,
    extractState
  ] = useSelector((state: IRootState) => [
    state.extractAdd,
    state.yearsState,
    state.extractState
  ], shallowEqual);
  const dispatch = useDispatch();
  const { mode } = extractAdd;
  const { t: translate } = useTranslation();

  /**
   * Set the edited extract detail, to be rendered after updation
   * @param currentExtract - current extract list
   * @param editedExtracted - extract detail to update particular extract
   * in the above extract list
   */
  const getExtractDetail = (currentExtract: IExtractList, editedExtracted: IExtract): void => {
    const newData: IExtractList = {
      ...currentExtract,
      extractName: editedExtracted.title.value,
      financialYear: editedExtracted.financialYear,
      isActive: editedExtracted.isActive,
      description: editedExtracted.description as string,
      surveyName: editedExtracted?.survey?.value as string,
      extractSchemaVersion: editedExtracted?.survey?.value as string
    };
    dispatch(ExtractListSlice.actions.selectedExtract(newData));
  };

  const scrollToTop = (): void => {
    const element = document.getElementById("extract-list");
    if (element) {
      element.scrollTop = 0;
    }
  };

  /**
   * Update extract list and total number of result after getting new extract data
   */
  React.useEffect(() => {
    dispatch(ExtractListSlice.actions.setExtractList([]));
    if (extractState.extractData) {
      const list = extractState.extractData.data.attributes.extractList;
      const currentExtract = extractState.selectedExtract;
      const editedExtracted = extractAdd.editExtractDetail;
      const sortedData = sortFunction(list);
      dispatch(ExtractListSlice.actions.setExtractList(sortedData));
      if (mode === ExtractModes.VIEW && list.length > 0) {
        if (currentExtract) {
          getExtractDetail(currentExtract, editedExtracted);
          dispatch(ExtractListSlice.actions.showExtractReports(true));
        }
      }
      else if (list.length > 0) {
        dispatch(ExtractListSlice.actions.showExtractReports(true));
      }
      else {
        dispatch(ExtractListSlice.actions.selectedExtract(null));
      }
      scrollToTop();
    }
  }, [extractState.extractData]);

  const getRequestParam = (): IGetExtractDetails => {
    return {
      selectedYear: yearsState.selectedYear,
      pageNumber: 1,
      resultPerPage: 100
    };
  };

  /**
   * Reuest extract api on change of financial year change
   */
  React.useEffect(() => {

    if (yearsState.selectedYear !== "") {
      GetExtractList(dispatch, getRequestParam());
    }
  }, [yearsState.selectedYear]);

  React.useEffect(() => {
    const searchtext = extractState.searchInput.trim().toLowerCase();
    if ((extractState.extractData) && (extractState.extractData.data.attributes.extractList.length > 0)) {
      const extractDataList = extractState.extractData.data.attributes.extractList;
      if (extractState.searchInput.length > 0) {
        const extractlist = extractDataList.filter((item: IExtractList): boolean => {
          const extractname = item.extractName.toString().toLowerCase();
          return extractname.includes(searchtext);
        });
        dispatch(ExtractListSlice.actions.setViewExtractList(extractlist));
      } else {
        dispatch(ExtractListSlice.actions.setViewExtractList(extractDataList));
      }

    } else {
      dispatch(ExtractListSlice.actions.setViewExtractList(extractState.extractList));
    }
  }, [extractState.searchInput]);

  React.useEffect(() => {
    if (extractState.extractList) {
      const { searchInput, extractList } = extractState;
      dispatch(ExtractListSlice.actions.setViewExtractList(
        getExtractListBasedOnSearchInput(searchInput, extractList)));
    }
  }, [extractState.extractList]);

  const checkSameExtractClicked = (item: IExtractList): boolean => {
    return item.extractGuid === extractState.selectedExtract?.extractGuid;
  };

  const handleSelectItem = (item: IExtractList): void => {
    try {
      dispatch(ExtractListSlice.actions.showExtractReports(true));
      dispatch(FinancialSlice.actions.selectedYear(item.financialYear));
    } catch (ex) {
      dispatch(ErrorAlertSlice.actions.showAlert(true));
    }
  };

  const handleChangeExtractItem = (item: IExtractList): void => {
    dispatch(ExtractAddSlice.actions.setExtractMode(ExtractModes.VIEW));
    const data = {
      id: item.extractGuid,
      year: item.financialYear
    };
    const availableTabs = document.querySelectorAll(".eui-dynamic-tabs__list-item").length > 2;
    dispatch(ExtractListSlice.actions.setClickedExtractIdDetails(data));
    if (!checkSameExtractClicked(item) && !availableTabs) {
      handleSelectItem(item);
    }
  };

  /**
 * To navigate to dashboard after user confirmation in prompt
 * @param item extract item
 */
  const onHandleAlertAction = (item: IExtractList): void => {
    onExtractCancelClick(dispatch, extractState.selectedExtract);
    handleChangeExtractItem(item);
  };

  /**
   * Handler for extract list change
   * @param item extract item
   */
  const handleOnClickItem = (item: IExtractList): void => {
    const validateCancelEdit = validateCloseEditExtract(
      extractAdd.editExtractDetail,
      extractAdd.editPreviousExtractDetail,
      extractAdd
    );
    if (validateCancelEdit.openAlert) {
      const errorContent: IErrorAlertObject = {
        title: `${translate("CloseAlert.Title")}`,
        message: [`${translate("CloseAlert.Message")}`],
        type: ErrorAlertType.WARNING,
        actionButtonText: translate("Button.Yes"),
        cancelButtonText: translate("Button.No"),
        actionClick: () => onHandleAlertAction(item)
      };
      dispatch(ErrorAlertSlice.actions.createErrorAlert(errorContent));
    } else{
      handleChangeExtractItem(item);
    }
  };

  return (
    <>
      <ExtractTreeListView
        extractList={extractState.viewExtractList}
        handleOnClickItem={handleOnClickItem} />
    </>
  );
};

export default ExtractTreeList;
